/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
/* eslint-disable react/jsx-props-no-spreading */
import { useSelector, useDispatch, batch } from 'react-redux';
import { usePaystackPayment } from 'react-paystack';
import React, { useContext, useState } from 'react';
import getCurrencySymbol from 'currency-symbol-map';
import PropTypes from 'prop-types';

import { FormPaymentFieldIndicator, PayButton, UiIndicator } from '../../common-field-styles';
import PaymentModalButton from '../../common-field-components/payment-modal-button';
import { confirmPayment, setUserInputOnCtrl } from '../../../form/reducer/form';
import PaymentForm from '../../../form/components/partials/payment-form';
import { onPaymentValueChange } from '../../../form/slice-actions/form';
import { FormContext } from '../../../shared/contexts/form-context';
import { getLabel } from '../../../../shared/form-lib';
import { ControlPropType } from '../../ctrl_proptype';
import { logToServer } from '../../../form/utils';

const PaystackSubComponent = ({ ctrl, formDesign, isRenderedOnModal }) => {
  const { inForm, currentPageNo } = useContext(FormContext);
  const dispatch = useDispatch();
  const {
    payments,
    userAmount,
    isPayOnSubmission,
    editableForm: { paystack_public_key, form_options, user_custom_texts, respondent_email },
  } = useSelector((state) => state.form);
  const { isPaid, isError, message } = payments[ctrl.type][ctrl.internal_id] || {};
  const modeStatusText = !form_options?.paystack_live_mode ? '(test mode)' : '';
  const submitButtonText =
    inForm && (formDesign.old.submission_button_text || getLabel('submit_text', user_custom_texts) || 'Submit');
  const [openModal, setOpenModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    email: respondent_email || '',
    phone: '',
    amount: ctrl.props.amount || '',
  });
  const { email, amount, phone } = paymentDetails;

  const finalAmount = Number(userAmount[ctrl.internal_id] ?? (ctrl.props.user_specify ? amount : ctrl.props.amount));

  const config = {
    reference: `formplus-${Math.random()}`,
    email,
    amount: finalAmount * 100,
    publicKey: paystack_public_key,
    metadata: {
      phone,
    },
  };

  const initializePayment = usePaystackPayment(config);

  const handleStorePaymentValue = (status, ref, displayMsg) => {
    const newValue = `Amount Paid: ${finalAmount} ${ctrl.props.currency}\nTransaction ID: ${ref}\n`;
    batch(() => {
      if (status === 'paid') {
        dispatch(
          setUserInputOnCtrl({
            newValue,
            property: 'value',
            page: currentPageNo - 1,
            ctrlId: ctrl.internal_id,
            isValid: true,
          })
        );

        if (ctrl.value !== newValue) {
          dispatch(onPaymentValueChange());
        }
      }

      dispatch(
        confirmPayment({
          type: ctrl.type,
          isPaid: status === 'paid',
          isError: status === 'error',
          message: displayMsg,
          ctrlId: ctrl.internal_id,
          transactionId: ref,
        })
      );
    });
  };

  const onSuccess = (response) => {
    const success = response.status === 'success';
    const payload = {
      form_id: window.FORM_ID,
      transaction_id: response.reference,
      amount: finalAmount,
      currency: ctrl.props.currency,
      status: success ? `success ${modeStatusText}` : `failure ${modeStatusText}`,
      reason: response.message,
      gateway: 'Paystack',
    };

    if (success) {
      const displayMsg = `Payment of ${finalAmount} ${ctrl.props.currency} approved. Kindly click on “${submitButtonText}” at the bottom of the form to complete transaction`;
      handleStorePaymentValue('paid', response.trxref, displayMsg);
    } else {
      handleStorePaymentValue('error', '', response.message || 'An error occured with your payment.');
    }

    logToServer(payload);
  };

  const handleChange = (name, value) => {
    setPaymentDetails((prev) => ({ ...prev, [name]: value }));
  };

  const startPayment = (e) => {
    e.preventDefault();
    if (!paystack_public_key) {
      return;
    }
    setOpenModal(false);
    initializePayment(onSuccess);
  };

  return (
    <>
      {isRenderedOnModal ? (
        <PaymentModalButton
          errorMessage={isError ? message || 'An error occured while paying.' : ''}
          onClick={() => inForm && paystack_public_key && setOpenModal(true)}
          isNoKeyAvailable={inForm && !paystack_public_key}
          isCompleted={!!(isPaid && ctrl.value)}
          amount={finalAmount}
          ctrl={ctrl}
        />
      ) : (
        <>
          {isPayOnSubmission ? (
            <FormPaymentFieldIndicator>
              There is a {ctrl.props.label} payment field attached to this form. You may be required to make payment of{' '}
              {`${getCurrencySymbol(ctrl.props.currency || 'USD')}${finalAmount}`} after submission.
            </FormPaymentFieldIndicator>
          ) : (
            <>
              {inForm && !paystack_public_key && (
                <UiIndicator className="status red">
                  <span>
                    The form owner has not set up their payment integration. Please contact the owner for more
                    information.
                  </span>
                </UiIndicator>
              )}

              {/* Messsage shown when error is returned from calling the payment APIs */}
              {isError && (
                <UiIndicator className="status red">
                  <span>{message || 'An error occured with your payment.'}</span>
                </UiIndicator>
              )}

              {!ctrl.value && !isPaid && (
                <PayButton
                  type="button"
                  id={`fpl-${ctrl.internal_id}`}
                  bgColor="#0ebf8a"
                  onClick={() => inForm && paystack_public_key && setOpenModal(true)}>
                  {ctrl.props.pay_button_text}
                </PayButton>
              )}

              {(isPaid || ctrl.value) && (
                <UiIndicator className="paid">
                  <i className="check icon" />
                  <span>{message || ctrl.value}</span>
                </UiIndicator>
              )}

              {inForm && !form_options?.paystack_live_mode && <UiIndicator className="mode">Test mode</UiIndicator>}
            </>
          )}
        </>
      )}

      {inForm && (
        <PaymentForm
          closeModal={() => setOpenModal(false)}
          handleOpenModal={() => setOpenModal(true)}
          openModal={openModal}
          gateway="paystack"
          ctrl={ctrl}
          startPayment={startPayment}
          handleChange={handleChange}
          paymentDetails={paymentDetails}
          message={message}
          finalAmount={finalAmount}
        />
      )}
    </>
  );
};

export default PaystackSubComponent;

PaystackSubComponent.defaultProps = {
  isRenderedOnModal: false,
  formDesign: null,
};

PaystackSubComponent.propTypes = {
  ctrl: PropTypes.shape(ControlPropType).isRequired,
  isRenderedOnModal: PropTypes.bool,
  formDesign: PropTypes.shape({
    old: PropTypes.object.isRequired,
    design: PropTypes.object.isRequired,
  }),
};
